/**
 * Version 1 of every consent document.
 *
 * These are the exact words a client reads. They are seeded into `consent_documents`, and from that
 * moment they are immutable: a change is a new version, never an edit, so a record written years ago
 * still resolves to the text that was actually on screen.
 *
 * DRAFTED FOR LEGAL REVIEW. Nothing here should go to production before counsel has read it —
 * see docs/13-delivery-phases.md, Phase 0.
 */
import type { ConsentGate } from './gates.js';

export interface ConsentDocumentSeed {
  readonly gate: ConsentGate;
  readonly version: 1;
  readonly locale: 'en';
  readonly title: string;
  /** The checkbox label. Short, plain, and never bundling two ideas into one tick. */
  readonly acknowledgement: string;
  /** The full text, markdown. */
  readonly body: string;
}

export const CONSENT_DOCUMENTS_V1: readonly ConsentDocumentSeed[] = [
  {
    gate: 'purchase_educational_nature',
    version: 1,
    locale: 'en',
    title: 'What this service is',
    acknowledgement:
      'I understand that Tooth Somatics is for educational and reflective purposes only. It is not a substitute for professional dental, medical, or mental-health advice, diagnosis, or treatment from a qualified provider.',
    body: `Tooth Somatics is an educational and reflective service.

It does not provide dental, medical, psychological, or psychiatric care, and it does not diagnose, treat, cure, or prevent anything.

What you are purchasing is a structured reflective exploration: a questionnaire, a personal chart, and a written reflection prepared by a practitioner. It is material to think with. It is not an assessment of your body, and it is not a professional opinion about your mouth.

If you have a concern about your health, please bring it to a licensed professional. Nothing here is offered in place of that.`,
  },
  {
    gate: 'no_clinical_claims',
    version: 1,
    locale: 'en',
    title: 'What this service never does',
    acknowledgement:
      'I understand that Tooth Somatics does not diagnose, treat, cure, or prevent any condition, and does not include clinical interpretation of X-rays or any dental advice or treatment planning.',
    body: `Tooth Somatics does not diagnose, treat, cure, or prevent any condition.

It does not read or interpret imaging of any kind, it does not give dental advice, and it does not plan care. No part of it creates a professional relationship with anyone licensed to examine you or to care for you.

What you receive is material for reflection, prepared by a person, offered as something to think with. It is never an assessment of your body and never an opinion about your mouth.

If you want an answer about your teeth, that answer belongs to a licensed professional who can examine you.`,
  },
  {
    gate: 'not_for_emergencies',
    version: 1,
    locale: 'en',
    title: 'If something is wrong right now',
    acknowledgement:
      'I understand this service is not for dental, medical, or psychological emergencies. If I am experiencing severe pain, swelling, distress, or any emergency, I will contact my licensed provider or 911 immediately.',
    body: `This is not a service to reach for in an emergency, and nobody here is monitoring for one.

Severe pain, swelling, bleeding, fever, an injury to your mouth, or thoughts of harming yourself are all reasons to stop and contact a licensed provider or your local emergency number now. In the United States that is 911.

Your reflection will still be here afterwards. Nothing about it is urgent, and nothing is lost by attending to something that is.`,
  },
  {
    gate: 'questionnaire_not_professional_care',
    version: 1,
    locale: 'en',
    title: 'This does not replace professional care',
    acknowledgement:
      'I understand that this process is educational and reflective and does not replace dental, medical, psychological, or psychiatric care.',
    body: `You are about to describe your own history in your own words.

Nothing you write here is reviewed as a professional would review it, and nothing you receive back is advice. This process does not replace professional medical, dental, or psychological care.

If something you notice about your mouth or your body concerns you, please consult a licensed professional. You can do that alongside this reflection; the two are not in competition.

There is no requirement to be exact or complete. "I am not sure" is a full answer.`,
  },
  {
    gate: 'emotional_awareness_responsibility',
    version: 1,
    locale: 'en',
    title: 'Emotional awareness and personal responsibility',
    acknowledgement:
      'I understand that memories, emotions, realizations, or family dynamics may surface during this process. I agree to approach it with honesty, discernment, and self-care, and to seek a licensed professional if I need support beyond this educational process.',
    body: `As you move through this process, memories, emotions, realizations, or family dynamics may begin to surface.

This work can bring awareness to places in your life that feel tender, uncomfortable, or confronting. That is not something to fear, but it is something to approach with honesty and gentleness.

Tooth Somatics does not tell you what to believe, and it does not assign blame to any person or family member. Insights that arise may affect how you view yourself, your relationships, your family system, or events in your life. How you interpret them, and what you do with them, is yours.

If you experience emotional distress, conflict in a relationship, or a sense of being overwhelmed, please seek support from a licensed mental health, medical, dental, or other appropriate professional.

You may stop at any point. Nothing you have written will be lost, and you are not required to explain why.`,
  },
  {
    gate: 'submission_interpretation_responsibility',
    version: 1,
    locale: 'en',
    title: 'How to hold what you receive',
    acknowledgement:
      'I understand that Tooth Somatics does not tell me what to believe and does not assign blame to any person or family member. I am responsible for how I interpret, integrate, communicate, or act on anything that arises.',
    body: `You are about to submit your reflection.

What comes back to you is educational material. It is not advice, not a finding about your body, and not a recommendation to take or avoid any action.

You are responsible for how you choose to interpret, integrate, communicate, or act on anything that arises from this process — including anything you decide to share with family members, and anything you decide to do about your mouth.

Please do not make a decision about your health on the basis of this reflection alone. Bring it to a licensed professional if it raises something you want to act on.`,
  },
  {
    gate: 'booking_educational_session',
    version: 1,
    locale: 'en',
    title: 'What a live session is',
    acknowledgement: 'I understand this consultation is educational and reflective.',
    body: `A live Tooth Somatics consultation is an educational and reflective conversation.

It is not a dental, medical, or psychological appointment, no diagnosis or treatment is provided, and nothing said in it should replace guidance from a licensed professional.

The conversation may move through family history and personal material. You choose what to bring and what to leave. You may end the session at any time.

The session is not recorded unless you are asked and you agree, separately, in advance.`,
  },
  {
    gate: 'photo_upload',
    version: 1,
    locale: 'en',
    title: 'Uploading images of your mouth',
    acknowledgement: 'I consent to uploading images of my mouth for this reflection.',
    body: `You may upload photographs of your own mouth to support your reflection.

They are stored privately and encrypted, are visible only to the practitioner preparing your chart, and are never published, shared, or used for marketing.

Location data is removed from every image when it arrives. You may delete any image before you submit, and you may ask for them to be deleted at any time afterwards.

Please upload only images of yourself.`,
  },
  {
    gate: 'sensitive_information_optional',
    version: 1,
    locale: 'en',
    title: 'Optional personal information',
    acknowledgement:
      'I understand these questions are optional, and I consent to my answers being stored securely.',
    body: `Some questions in this questionnaire invite information that is personal and sensitive — family events, losses, and pregnancies that did not continue.

Every one of these questions is optional. You may skip any of them and continue, and skipping them does not reduce what you receive.

If you choose to answer, your responses are encrypted, are visible only to the practitioner preparing your chart, and are never used for anything other than your own reflection.

You may withdraw this consent later, and the information you gave under it will be deleted.`,
  },
  {
    gate: 'terms_of_service',
    version: 1,
    locale: 'en',
    title: 'Terms of service',
    acknowledgement: 'I agree to the terms of service.',
    body: `PLACEHOLDER — to be drafted with counsel before launch.

Must cover: what is purchased and what is delivered, the educational framing, turnaround expectations, refund and cancellation terms, acceptable use, intellectual property in the chart you receive, limitation of liability, and the governing jurisdiction.`,
  },
  {
    gate: 'privacy_policy',
    version: 1,
    locale: 'en',
    title: 'Privacy policy',
    acknowledgement: 'I have read the privacy policy.',
    body: `PLACEHOLDER — to be drafted with counsel before launch.

Must cover: what is collected and why, the lawful basis for each category, processors (payment, scheduling, hosting, email), retention periods including the ten-year retention of consent records, international transfers, your rights of access, correction, export and erasure, what survives an erasure request and why, and how to contact us about any of it.`,
  },
  {
    gate: 'marketing_optional',
    version: 1,
    locale: 'en',
    title: 'Occasional letters',
    acknowledgement: 'Yes, I would like to receive occasional letters from Tooth Somatics.',
    body: `This is entirely optional and has no effect on your reflection or your session.

If you say yes, you may receive occasional writing about the work — a few times a year, never more than monthly. Every letter carries a one-click way to stop, and stopping takes effect immediately.

If you say no, you will still receive the messages that concern your own purchase: your receipt, your account, and word that your chart is ready.`,
  },
];

export const CONSENT_DOCUMENTS_BY_GATE: ReadonlyMap<ConsentGate, ConsentDocumentSeed> = new Map(
  CONSENT_DOCUMENTS_V1.map((document) => [document.gate, document]),
);

export function consentDocument(gate: ConsentGate): ConsentDocumentSeed {
  const document = CONSENT_DOCUMENTS_BY_GATE.get(gate);
  if (!document) throw new Error(`No consent document seeded for gate: ${gate}`);
  return document;
}
